import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { Icon } from "../components/icon";
import { Kbd } from "../components/states";
import type { IconName } from "../icons";

export interface CommandItem {
  id: string;
  label: string;
  icon: IconName;
  href: string;
  /** Group heading (items keep their given order; groups appear in first-seen order). */
  group?: string;
  /** Extra search terms. */
  keywords?: readonly string[];
  /** Shortcut hint keys, e.g. ["G", "D"]. */
  shortcut?: readonly string[];
}

/** Groups items by `group`, preserving first-seen group order. Ungrouped items come first. */
export function groupCommandItems(items: readonly CommandItem[]): { heading: string | undefined; items: CommandItem[] }[] {
  const groups = new Map<string | undefined, CommandItem[]>();
  for (const item of items) {
    const list = groups.get(item.group);
    if (list) list.push(item);
    else groups.set(item.group, [item]);
  }
  const ordered = [...groups.entries()].map(([heading, list]) => ({ heading, items: list }));
  return ordered.sort((a, b) => Number(a.heading !== undefined) - Number(b.heading !== undefined));
}

export interface CommandPaletteLabels {
  title: string;
  placeholder: string;
  empty: string;
  navigate: string;
  open: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly CommandItem[];
  onSelect: (item: CommandItem) => void;
  labels: CommandPaletteLabels;
}

/** Registers Ctrl/Cmd + K to open the palette. */
export function useCommandPaletteHotkey(onOpen: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpen();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onOpen]);
}

function PaletteItem({ item, onSelect }: { item: CommandItem; onSelect: (item: CommandItem) => void }) {
  return (
    <Command.Item
      value={`${item.label} ${item.id}`}
      keywords={item.keywords ? [...item.keywords] : undefined}
      onSelect={() => {
        onSelect(item);
      }}
      className="group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm text-fg-muted data-[selected=true]:bg-primary-soft data-[selected=true]:text-fg"
    >
      <Icon name={item.icon} />
      {item.label}
      {item.shortcut ? (
        <span className="ml-auto flex gap-1">
          {item.shortcut.map((key) => (
            <Kbd key={key}>{key}</Kbd>
          ))}
        </span>
      ) : null}
      <Icon name="arrow-right" className={item.shortcut ? "size-3.5 opacity-0 group-data-[selected=true]:opacity-100" : "ml-auto size-3.5 opacity-0 group-data-[selected=true]:opacity-100"} />
    </Command.Item>
  );
}

/** cmdk palette inside the token modal (`#cmdk` in app.js). The query clears on close / select. */
export function CommandPalette({ open, onOpenChange, items, onSelect, labels }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };
  const handleSelect = (item: CommandItem) => {
    setQuery("");
    onSelect(item);
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="modal-backdrop items-start pt-[12vh]">
          <DialogPrimitive.Content className="modal max-w-xl overflow-hidden outline-none" aria-describedby={undefined}>
            <DialogPrimitive.Title className="sr-only">{labels.title}</DialogPrimitive.Title>
            <Command label={labels.title} loop>
              <div className="flex items-center gap-3 border-b border-line px-4">
                <Icon name="search" className="text-fg-subtle" />
                <Command.Input value={query} onValueChange={setQuery} aria-label={labels.placeholder} className="h-12 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle" placeholder={labels.placeholder} />
                <Kbd>Esc</Kbd>
              </div>
              <Command.List className="max-h-80 overflow-y-auto p-2">
                <Command.Empty className="empty py-8">{labels.empty}</Command.Empty>
                {groupCommandItems(items).map((group) => (
                  <Command.Group
                    key={group.heading ?? "__ungrouped"}
                    heading={group.heading}
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-fg-subtle"
                  >
                    {group.items.map((item) => (
                      <PaletteItem key={item.id} item={item} onSelect={handleSelect} />
                    ))}
                  </Command.Group>
                ))}
              </Command.List>
              <div className="flex gap-3 border-t border-line px-4 py-2 text-[11px] text-fg-subtle">
                <span>
                  <Kbd>↑</Kbd> <Kbd>↓</Kbd> {labels.navigate}
                </span>
                <span>
                  <Kbd>Enter</Kbd> {labels.open}
                </span>
              </div>
            </Command>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
